// ---------------------------------------------------------------------------
// Firebase client bootstrap
// ---------------------------------------------------------------------------
// Single initialisation point for the Firebase SDK. Everything that touches
// Firestore, Auth, Storage or Callable Functions imports from here — never
// calls initializeApp() itself.
//
// Config comes from REACT_APP_FIREBASE_* env vars (see frontend/.env.example).
// These are public client identifiers, not secrets: access is enforced by
// firestore.rules and storage.rules.
// ---------------------------------------------------------------------------
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Fail loudly in development if the env file was never created — otherwise the
// SDK throws a much less obvious error deep inside a query.
const missing = Object.entries(firebaseConfig)
  .filter(([, v]) => !v)
  .map(([k]) => k);

if (missing.length && process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.error(
    `[firebase] Missing config: ${missing.join(", ")}. ` +
      "Copy frontend/.env.example to frontend/.env and fill it in."
  );
}

const USE_EMULATORS = process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true";

// getApps() guard keeps hot-module reload from re-initialising the app.
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const db = getFirestore(app);
export const storage = getStorage(app);
// Functions are deployed to the Singapore region to sit next to Firestore.
export const functions = getFunctions(app, "asia-southeast1");

if (USE_EMULATORS) {
  // Ports mirror the `emulators` block in firebase.json.
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectStorageEmulator(storage, "127.0.0.1", 9199);
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  // eslint-disable-next-line no-console
  console.info("[firebase] Connected to local emulator suite.");
}

// ---------------------------------------------------------------------------
// Auth — created on demand, not at module load
// ---------------------------------------------------------------------------
// getAuth() pulls in Google's gapi iframe (apis.google.com), which sets
// third-party cookies. Only the admin panel signs anyone in, so initialising
// it eagerly meant every public visitor loaded that iframe for nothing.
// Calling this from the admin entry point keeps it off the public site.
let authInstance = null;

export function getAuthInstance() {
  if (!authInstance) {
    authInstance = getAuth(app);
    if (USE_EMULATORS) {
      connectAuthEmulator(authInstance, "http://127.0.0.1:9099", {
        disableWarnings: true,
      });
    }
  }
  return authInstance;
}

export default app;
